import React, { useEffect } from 'react';
import { formatTelefonGoster } from '../utils/phoneNormalize';
import { parseApiDate } from '../utils/security';
import './BayilikBasvuruDetayModal.css';

const DURUM_LABELS = {
  onay_bekliyor: 'Onay Bekliyor',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
};

const DURUM_BADGE = {
  onay_bekliyor: { bg: '#fef3c7', color: '#92400e' },
  onaylandi: { bg: '#d1fae5', color: '#065f46' },
  reddedildi: { bg: '#fee2e2', color: '#991b1b' },
};

const DAVET_DURUM_LABELS = {
  aktif: 'Aktif',
  suresi_doldu: 'Süresi doldu',
  kilitli: 'Kilitli',
  gonderildi: 'Gönderildi',
  pasif: 'Pasif',
};

const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

function DurumBadge({ durum }) {
  const style = DURUM_BADGE[durum] || { bg: '#f1f5f9', color: '#334155' };
  return (
    <span className="bbd-header-badge" style={{ backgroundColor: style.bg, color: style.color }}>
      {DURUM_LABELS[durum] || durum}
    </span>
  );
}

const Field = ({ label, children, full }) => (
  <div className={`bbd-field ${full ? 'bbd-field-full' : ''}`}>
    <span className="bbd-field-label">{label}</span>
    <div className="bbd-field-value">{children}</div>
  </div>
);

const BayilikBasvuruDetayModal = ({
  detail,
  davetDetail,
  loading,
  error,
  onClose,
  onOnayla,
  onReddet,
  redNedeni,
  onRedNedeniChange,
  actionLoading = false,
  pasifLoading = false,
  onPasifAl,
  mode = 'basvuru',
}) => {
  const isDavet = mode === 'davet';
  const canOnayRed = !isDavet && detail?.durum === 'onay_bekliyor';
  const canPasifAl = isDavet && davetDetail?.durum === 'aktif';
  const footerBusy = actionLoading || pasifLoading;

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !footerBusy) onClose?.();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, footerBusy]);

  const baslik = isDavet
    ? 'Davet Linki'
    : (detail?.bayiAdi || 'Bayi Başvurusu');

  return (
    <div className="modal-overlay bayilik-basvuru-detay-modal" onClick={onClose} role="presentation">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>
            <i className={`fas ${isDavet ? 'fa-link' : 'fa-store'}`} />
            {baslik}
            {!isDavet && detail?.durum ? <DurumBadge durum={detail.durum} /> : null}
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Detay yükleniyor</div>
            </div>
          )}

          {!loading && error && <div className="error-state">{error}</div>}

          {!loading && !error && isDavet && davetDetail && (
            <div className="bbd-content">
              <section className="bbd-section">
                <div className="bbd-section-title">
                  <i className="fas fa-link" />
                  Link Bilgileri
                </div>
                <div className="bbd-grid">
                  <Field label="Link" full>
                    <a href={davetDetail.url} target="_blank" rel="noopener noreferrer" className="bbd-link">
                      {davetDetail.url}
                    </a>
                  </Field>
                  <Field label="Durum">{DAVET_DURUM_LABELS[davetDetail.durum] || davetDetail.durum}</Field>
                  <Field label="Geçerlilik">{formatTarih(davetDetail.gecerlilikBitis)}</Field>
                  <Field label="Oluşturma">{formatTarih(davetDetail.createdAt)}</Field>
                  <Field label="Gönderim">{formatTarih(davetDetail.gonderimTarihi)}</Field>
                  <Field label="Oluşturan">
                    {davetDetail.olusturanKullanici?.adSoyad
                      || davetDetail.olusturanKullanici?.kullaniciAdi
                      || '—'}
                  </Field>
                </div>
              </section>
            </div>
          )}

          {!loading && !error && !isDavet && detail && (
            <div className="bbd-content">
              <section className="bbd-section">
                <div className="bbd-section-title">
                  <i className="fas fa-building" />
                  Bayi Bilgileri
                </div>
                <div className="bbd-grid">
                  <Field label="Bayi Adı">{detail.bayiAdi || '—'}</Field>
                  <Field label="Yetkili Kişi">{detail.yetkiliKisi || '—'}</Field>
                  <Field label="Telefon">{formatTelefonGoster(detail.telefon)}</Field>
                  <Field label="E-posta">{detail.mail || '—'}</Field>
                  <Field label="Kullanıcı Adı">{detail.kullaniciAdi || '—'}</Field>
                </div>
              </section>

              <section className="bbd-section">
                <div className="bbd-section-title">
                  <i className="fas fa-map-marker-alt" />
                  Adres
                </div>
                <div className="bbd-grid">
                  <Field label="İl">{detail.ilAdi || '—'}</Field>
                  <Field label="İlçe">{detail.ilceAdi || '—'}</Field>
                  <Field label="Açık Adres" full>{detail.adres || '—'}</Field>
                </div>
              </section>

              <section className="bbd-section">
                <div className="bbd-section-title">
                  <i className="fas fa-clock" />
                  Başvuru
                </div>
                <div className="bbd-grid">
                  <Field label="Başvuru Tarihi">{formatTarih(detail.createdAt)}</Field>
                  <Field label="Son Güncelleme">{formatTarih(detail.updatedAt)}</Field>
                  <Field label="Durum">{DURUM_LABELS[detail.durum] || detail.durum}</Field>
                  {detail.onayTarihi && (
                    <Field label="İşlem Tarihi">{formatTarih(detail.onayTarihi)}</Field>
                  )}
                  {detail.onaylayanKullanici && (
                    <Field label="İşlemi Yapan">
                      {detail.onaylayanKullanici.adSoyad || detail.onaylayanKullanici.kullaniciAdi}
                    </Field>
                  )}
                  {detail.olusturulanKullanici && (
                    <Field label="Bayi Hesabı">{detail.olusturulanKullanici.kullaniciAdi}</Field>
                  )}
                  {detail.redNedeni && (
                    <Field label="Red Nedeni" full>{detail.redNedeni}</Field>
                  )}
                </div>
              </section>

              {detail.davet && (
                <section className="bbd-section">
                  <div className="bbd-section-title">
                    <i className="fas fa-envelope-open-text" />
                    Davet
                  </div>
                  <div className="bbd-grid">
                    <Field label="Davet Linki" full>
                      <a
                        href={detail.davet.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bbd-link"
                      >
                        {detail.davet.url}
                      </a>
                    </Field>
                    <Field label="Davet Durumu">
                      {DAVET_DURUM_LABELS[detail.davet.durum] || detail.davet.durum}
                    </Field>
                    <Field label="Link Geçerlilik">{formatTarih(detail.davet.gecerlilikBitis)}</Field>
                    <Field label="Link Oluşturan">
                      {detail.davet.olusturanKullanici?.adSoyad
                        || detail.davet.olusturanKullanici?.kullaniciAdi
                        || detail.olusturanKullanici?.kullaniciAdi
                        || '—'}
                    </Field>
                  </div>
                </section>
              )}

              {canOnayRed && (
                <section className="bbd-section bbd-section-red">
                  <div className="bbd-section-title">
                    <i className="fas fa-comment-alt" />
                    Red Nedeni
                  </div>
                  <textarea
                    id="bayilik-red-nedeni"
                    className="bbd-red-textarea"
                    value={redNedeni}
                    onChange={(e) => onRedNedeniChange?.(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Reddetmek için neden yazın"
                  />
                </section>
              )}
            </div>
          )}
        </div>

        {canPasifAl && (
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger"
              onClick={onPasifAl}
              disabled={footerBusy}
            >
              {pasifLoading ? 'Pasife alınıyor...' : 'Pasife al'}
            </button>
          </div>
        )}

        {canOnayRed && (
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger"
              onClick={onReddet}
              disabled={actionLoading}
            >
              Reddet
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onOnayla}
              disabled={actionLoading}
            >
              Onayla
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BayilikBasvuruDetayModal;
